import type { PolicyGroupDetail, PolicySummary } from '../../lib/api'

/**
 * How a builder group reads on the list and on its own page, worked out from what the server sent.
 *
 * Nothing here counts a group from rows on a page. The list's group size is the server's, and the
 * group page reads the whole group in one request, so "3 of 4 approved" is a statement about the
 * group rather than about whatever happened to be loaded (D-430, D-437).
 */

export type ListSegment =
  | { kind: 'policy'; policy: PolicySummary }
  | { kind: 'group'; groupId: string; name: string; size: number; policies: PolicySummary[] }

/**
 * The rows as the list draws them: a policy on its own, or a group heading over its members.
 *
 * The server sorts siblings together, so a group is a run of consecutive rows. A run is read rather
 * than collecting every row with the same id, because a group split across a page edge must show as
 * the part on this page with its true size beside it, not be reassembled from rows that are not here.
 */
export function segmentPolicies(rows: readonly PolicySummary[]): ListSegment[] {
  const segments: ListSegment[] = []
  for (const policy of rows) {
    const last = segments[segments.length - 1]
    if (policy.groupId && last?.kind === 'group' && last.groupId === policy.groupId) {
      last.policies.push(policy)
      continue
    }
    if (policy.groupId)
      segments.push({
        kind: 'group',
        groupId: policy.groupId,
        name: policy.groupName ?? policy.name,
        size: policy.groupSize ?? 1,
        policies: [policy]
      })
    else segments.push({ kind: 'policy', policy })
  }
  return segments
}

/** "4 policies", or "2 of 4 shown" when the page holds only part of the group. */
export function groupCountLabel(shown: number, size: number): string {
  if (shown < size) return `${shown} of ${size} shown`
  return `${size} ${size === 1 ? 'policy' : 'policies'}`
}

export interface GroupProgress {
  active: number
  approved: number
  customized: number
  /** Every active member's newest revision is approved, so Publish all can run. */
  readyToPublish: boolean
  /** At least one active member has a draft newer than anything approved. */
  awaitingApproval: boolean
  summary: string
}

export function groupProgress(group: PolicyGroupDetail): GroupProgress {
  const active = group.members.filter((member) => member.status === 'active')
  const approved = active.filter((member) => member.latestStatus === 'approved').length
  const customized = active.filter((member) => member.customized).length

  let summary: string
  if (group.status === 'retired' || active.length === 0) summary = 'Retired. Nothing in this group can be edited or published.'
  else if (approved === active.length) summary = `All ${active.length} approved and ready to publish.`
  else if (approved === 0) summary = `${active.length} ${active.length === 1 ? 'draft' : 'drafts'} waiting for approval. A draft reaches no device.`
  else summary = `${approved} of ${active.length} approved. The rest are drafts, and a draft reaches no device.`

  return {
    active: active.length,
    approved,
    customized,
    readyToPublish: active.length > 0 && approved === active.length,
    awaitingApproval: approved < active.length,
    summary
  }
}

/** The member's place in the group, in words: "Claude in a browser" or "Screenshot for Claude in a browser". */
export function memberLabel(member: PolicyGroupDetail['members'][number]): string {
  const where = member.agentDisplayName ?? member.name
  return member.role === 'screenshot' ? `Screenshot for ${where}` : where
}

/** Which violation message a group shows, read from the members that tell the person. */
export interface GroupMessage {
  /** How many active members tell the person; with none, there is nothing to choose. */
  notifying: number
  /** The message they all share, or null when they all use their own words. */
  chosen: string | null
  /** True when they disagree, which happens once somebody chooses one under a member's Advanced settings. */
  mixed: boolean
}

export function groupMessage(group: PolicyGroupDetail): GroupMessage {
  const notifying = group.members.filter((member) => member.status === 'active' && member.notifies === true)
  const ids = [...new Set(notifying.map((member) => member.violationMessageId ?? null))]
  return { notifying: notifying.length, chosen: ids.length === 1 ? ids[0]! : null, mixed: ids.length > 1 }
}
